import type { User } from 'firebase/auth';
import {
  createUserWithEmailAndPassword,
  onIdTokenChanged,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { auth, db, googleProvider } from './firebase';
import type { AppUserProfile } from '../types/content';

type AuthContextValue = {
  loading: boolean;
  user: User | null;
  profile: AppUserProfile | null;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (displayName: string, email: string, password: string) => Promise<void>;
  signOutUser: () => Promise<void>;
  clearError: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function avatarUrlFor(user: User, displayNameOverride?: string): string {
  if (user.photoURL) return user.photoURL;
  const seed = displayNameOverride || user.displayName || user.email || user.uid;
  return `https://api.dicebear.com/9.x/initials/png?seed=${encodeURIComponent(seed)}`;
}

export async function ensureUserProfile(user: User, displayNameOverride?: string): Promise<AppUserProfile> {
  const ref = doc(db, 'users', user.uid);
  const snapshot = await getDoc(ref);
  const displayName = (displayNameOverride || user.displayName || '').trim();
  const baseProfile = {
    uid: user.uid,
    email: user.email || '',
    displayName,
    avatarUrl: avatarUrlFor(user, displayName),
    emailVerifiedLastSeen: user.emailVerified,
    lastLogin: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  if (snapshot.exists()) {
    await setDoc(ref, baseProfile, { merge: true });
    const data = snapshot.data();
    return {
      uid: user.uid,
      email: baseProfile.email,
      displayName: baseProfile.displayName,
      avatarUrl: baseProfile.avatarUrl,
      role: String(data.role || 'user'),
      emailVerifiedLastSeen: user.emailVerified,
      clipCreditsBalance: Number(data.clipCreditsBalance || 0),
    };
  }

  const newProfile = {
    ...baseProfile,
    role: 'user',
    clipCreditsBalance: 0,
    createdAt: serverTimestamp(),
  };
  await setDoc(ref, newProfile, { merge: true });
  return {
    uid: user.uid,
    email: newProfile.email,
    displayName: newProfile.displayName,
    avatarUrl: newProfile.avatarUrl,
    role: 'user',
    emailVerifiedLastSeen: user.emailVerified,
    clipCreditsBalance: 0,
  };
}

function mapProfile(uid: string, data: Record<string, unknown>): AppUserProfile {
  return {
    uid,
    email: String(data.email || ''),
    displayName: String(data.displayName || ''),
    avatarUrl: String(data.avatarUrl || ''),
    role: String(data.role || 'user'),
    emailVerifiedLastSeen: data.emailVerifiedLastSeen === true,
    clipCreditsBalance: Number(data.clipCreditsBalance || 0),
  };
}

function readableAuthError(error: unknown): string {
  const code = typeof error === 'object' && error && 'code' in error ? String(error.code) : '';
  if (code.includes('invalid-credential') || code.includes('wrong-password') || code.includes('user-not-found')) {
    return 'Invalid email or password.';
  }
  if (code.includes('email-already-in-use')) return 'That email already has an account.';
  if (code.includes('popup-closed-by-user')) return 'Sign-in was closed before it finished.';
  if (code.includes('weak-password')) return 'Use at least 6 characters.';
  if (code.includes('invalid-email')) return 'Enter a valid email address.';
  return 'Something went wrong. Try again.';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<AppUserProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribeProfile: (() => void) | undefined;
    const unsubscribeAuth = onIdTokenChanged(auth, async (nextUser) => {
      unsubscribeProfile?.();
      unsubscribeProfile = undefined;
      setUser(nextUser);
      setProfile(null);

      if (!nextUser) {
        setLoading(false);
        return;
      }

      try {
        const ensured = await ensureUserProfile(nextUser);
        setProfile(ensured);
        unsubscribeProfile = onSnapshot(doc(db, 'users', nextUser.uid), (snapshot) => {
          if (snapshot.exists()) setProfile(mapProfile(nextUser.uid, snapshot.data()));
        });
      } catch (profileError) {
        setError(readableAuthError(profileError));
      } finally {
        setLoading(false);
      }
    });

    return () => {
      unsubscribeProfile?.();
      unsubscribeAuth();
    };
  }, []);

  const withAuthError = useCallback(async (action: () => Promise<void>) => {
    setError(null);
    try {
      await action();
    } catch (authError) {
      setError(readableAuthError(authError));
      throw authError;
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      loading,
      user,
      profile,
      error,
      clearError: () => setError(null),
      signInWithGoogle: () =>
        withAuthError(async () => {
          const credential = await signInWithPopup(auth, googleProvider);
          if (credential.user) await ensureUserProfile(credential.user);
        }),
      signInWithEmail: (email, password) =>
        withAuthError(async () => {
          await signInWithEmailAndPassword(auth, email.trim(), password);
        }),
      registerWithEmail: (displayName, email, password) =>
        withAuthError(async () => {
          const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
          await updateProfile(credential.user, { displayName: displayName.trim() });
          await sendEmailVerification(credential.user);
          await ensureUserProfile(credential.user, displayName);
        }),
      signOutUser: () => signOut(auth),
    }),
    [error, loading, profile, user, withAuthError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider.');
  return value;
}
